import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '~/lib/utils'

export interface CustomSelectOption {
    value: string
    label: string
}

export interface CustomSelectProps {
    id?: string
    value: string
    options: CustomSelectOption[]
    onValueChange(value: string): void
    placeholder?: string
    ariaLabel?: string
    disabled?: boolean
    containerClassName?: string
    className?: string
    /** 드롭다운 안에서 바로 검색(필터)할 수 있게 한다. 옵션이 많을 때(예: 평가 선택). */
    searchable?: boolean
    /** 검색 입력 placeholder. */
    searchPlaceholder?: string
}

/** 브라우저 기본 select 대신 서비스 톤을 유지하는 단일 선택 컴포넌트(검색 옵션 포함). */
export function CustomSelect({
    id,
    value,
    options,
    onValueChange,
    placeholder = '선택하세요',
    ariaLabel,
    disabled,
    containerClassName,
    className,
    searchable = false,
    searchPlaceholder = '검색…',
}: CustomSelectProps) {
    const generatedId = useId()
    const listboxId = `${id ?? generatedId}-listbox`
    const rootRef = useRef<HTMLDivElement>(null)
    const searchRef = useRef<HTMLInputElement>(null)
    const [open, setOpen] = useState(false)
    const [dropUp, setDropUp] = useState(false)
    const [query, setQuery] = useState('')
    // 드롭다운을 fixed로 띄워 모달/overflow 컨테이너에 잘리지 않게 한다(트리거 rect 기준).
    const [menu, setMenu] = useState<{
        left: number
        width: number
        top?: number
        bottom?: number
    } | null>(null)

    // 검색어로 필터(searchable일 때만). highlightedIndex는 필터된 목록 기준.
    const filtered = useMemo(() => {
        if (!searchable || !query.trim()) return options
        const q = query.trim().toLowerCase()
        return options.filter((o) => o.label.toLowerCase().includes(q))
    }, [options, query, searchable])

    const selected = options.find((option) => option.value === value) ?? null
    const [highlightedIndex, setHighlightedIndex] = useState(0)

    useEffect(() => {
        if (!open) return
        const onPointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('pointerdown', onPointerDown)
        return () => document.removeEventListener('pointerdown', onPointerDown)
    }, [open])

    // 열릴 때 검색어 초기화 + 검색 입력 포커스.
    useEffect(() => {
        if (!open) return
        setQuery('')
        const idx = filtered.findIndex((o) => o.value === value)
        setHighlightedIndex(Math.max(idx, 0))
        if (searchable) {
            const t = setTimeout(() => searchRef.current?.focus(), 0)
            return () => clearTimeout(t)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // 트리거 rect로 드롭다운 fixed 좌표를 계산한다(아래 공간이 부족하면 위로 펼침).
    const positionMenu = () => {
        const rect = rootRef.current?.getBoundingClientRect()
        if (!rect) return
        const spaceBelow = window.innerHeight - rect.bottom
        const up = spaceBelow < 300 && rect.top > spaceBelow
        setDropUp(up)
        setMenu({
            left: rect.left,
            width: rect.width,
            ...(up
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
        })
    }

    const openMenu = () => {
        if (disabled || options.length === 0) return
        positionMenu()
        setOpen(true)
    }

    // 열린 동안 스크롤·리사이즈에 맞춰 위치를 갱신한다(모달 body 스크롤도 잡게 capture).
    useEffect(() => {
        if (!open) return
        const reposition = () => positionMenu()
        window.addEventListener('resize', reposition)
        window.addEventListener('scroll', reposition, true)
        return () => {
            window.removeEventListener('resize', reposition)
            window.removeEventListener('scroll', reposition, true)
        }
    }, [open])

    const selectOption = (nextIndex: number) => {
        const option = filtered[nextIndex]
        if (!option) return
        onValueChange(option.value)
        setOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return

        if (!open) {
            if (
                event.key === 'ArrowDown' ||
                event.key === 'ArrowUp' ||
                event.key === 'Enter' ||
                event.key === ' '
            ) {
                event.preventDefault()
                openMenu()
            }
            return
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            setOpen(false)
            return
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setHighlightedIndex((current) =>
                filtered.length ? (current + 1) % filtered.length : 0
            )
            return
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlightedIndex((current) =>
                filtered.length
                    ? (current - 1 + filtered.length) % filtered.length
                    : 0
            )
            return
        }

        if (event.key === 'Home') {
            event.preventDefault()
            setHighlightedIndex(0)
            return
        }

        if (event.key === 'End') {
            event.preventDefault()
            setHighlightedIndex(Math.max(filtered.length - 1, 0))
            return
        }

        if (event.key === 'Enter') {
            event.preventDefault()
            selectOption(highlightedIndex)
        }
        // 검색 입력에서 Space는 글자 입력이어야 하므로 Space 선택은 비활성(검색 가능 시).
        if (event.key === ' ' && !searchable) {
            event.preventDefault()
            selectOption(highlightedIndex)
        }
    }

    return (
        <div
            ref={rootRef}
            className={cn('relative', containerClassName)}
            onKeyDown={handleKeyDown}
            data-slot='custom-select'>
            <button
                id={id}
                type='button'
                aria-label={ariaLabel}
                aria-haspopup='listbox'
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                disabled={disabled}
                onClick={() => (open ? setOpen(false) : openMenu())}
                className={cn(
                    'border-input bg-card text-foreground focus-visible:ring-ring flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-left text-sm transition-colors outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
                    open ? 'border-primary ring-ring ring-2' : '',
                    className
                )}>
                <span
                    className={cn(
                        'min-w-0 truncate',
                        selected ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                    {selected?.label ?? placeholder}
                </span>
                <ChevronDown
                    className={cn(
                        'text-muted-foreground size-4 shrink-0 transition-transform',
                        open ? 'rotate-180' : ''
                    )}
                    aria-hidden='true'
                />
            </button>

            {open && menu ? (
                <div
                    style={{
                        position: 'fixed',
                        left: menu.left,
                        width: menu.width,
                        ...(dropUp
                            ? { bottom: menu.bottom }
                            : { top: menu.top }),
                    }}
                    className={cn(
                        'border-border bg-card z-50 overflow-hidden rounded-md border shadow-lg'
                    )}>
                    {searchable ? (
                        <div className='border-border relative border-b p-1.5'>
                            <Search className='text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-3.5 -translate-y-1/2' />
                            <input
                                ref={searchRef}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value)
                                    setHighlightedIndex(0)
                                }}
                                placeholder={searchPlaceholder}
                                className='border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-ring h-8 w-full rounded border pr-2 pl-8 text-sm outline-none focus-visible:ring-2'
                            />
                        </div>
                    ) : null}
                    <ul
                        id={listboxId}
                        role='listbox'
                        className='grid max-h-60 gap-1 overflow-y-auto p-1'>
                        {filtered.length === 0 ? (
                            <li className='text-muted-foreground px-2.5 py-2 text-sm'>
                                결과가 없어요
                            </li>
                        ) : (
                            filtered.map((option, index) => {
                                const isSelected = option.value === value
                                const highlighted = index === highlightedIndex
                                return (
                                    <li
                                        key={option.value}
                                        role='option'
                                        aria-selected={isSelected}>
                                        <button
                                            type='button'
                                            onMouseEnter={() =>
                                                setHighlightedIndex(index)
                                            }
                                            onClick={() => selectOption(index)}
                                            className={cn(
                                                'flex w-full items-center justify-between gap-2 rounded px-2.5 py-2 text-left text-sm transition-colors',
                                                highlighted
                                                    ? 'bg-accent text-accent-foreground'
                                                    : 'text-foreground hover:bg-accent/70'
                                            )}>
                                            <span className='min-w-0 truncate'>
                                                {option.label}
                                            </span>
                                            {isSelected ? (
                                                <Check
                                                    className='text-primary size-4 shrink-0'
                                                    aria-hidden='true'
                                                />
                                            ) : null}
                                        </button>
                                    </li>
                                )
                            })
                        )}
                    </ul>
                </div>
            ) : null}
        </div>
    )
}
